import { useCallback, useState } from "react";

import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useViewsOptional } from "#app/lib/views/view-context";
import { viewPath } from "#app/lib/views/view-routes";
import { SaveViewControl } from "./save-view-control";

/**
 * The saved-view mount of `SaveViewControl`, and THE only place a view's
 * display options are committed.
 *
 * The panel writes nothing — a toggle there previews immediately and arrives
 * here as dirty keys — so the decision to keep an edit is made once, in the
 * toolbar, beside the view it changes. A footer under whichever panel page
 * happened to be open would put that decision somewhere different depending
 * on how the person got there. Absent while the draft matches what is saved.
 */
export function ViewSaveButton() {
  const views = useViewsOptional();
  const { replace } = useAppNavigate();
  const [forking, setForking] = useState(false);

  const activeView = views?.activeView ?? null;
  const config = views?.config;
  const scope = views?.scope;
  const createView = views?.createView;
  const discard = views?.discard;
  const discardChanges = views?.discardChanges;

  // The fork carries the DRAFT, so the new view opens showing exactly what
  // the person was looking at when they decided to keep it. The edit then
  // belongs to that view, and the one it came from returns to its saved state.
  const handleSaveAsNew = useCallback(async () => {
    if (!activeView || !createView || !discard || !scope || !config) return;
    setForking(true);
    try {
      const created = await createView(
        { type: activeView.type, config: { ...config } },
        "save_as_new_tab",
      );
      if (!created) return;
      discard();
      replace(viewPath(scope, created));
    } finally {
      setForking(false);
    }
  }, [activeView, config, createView, discard, replace, scope]);

  // Nothing to save outside a saved-view scope — the team and member issue
  // screens keep their config in session state.
  if (!views || !activeView || !discardChanges) return null;
  if (views.dirtyKeys.length === 0) return null;

  const { canManage, saving, save } = views;
  // No `create.views`, no control: the Filters page edits the same draft and
  // is open to everyone, but for this viewer the edit is a preview — the
  // filter panel's own clear puts it back, and leaving the view retires it.
  // A Save they could never press, and a menu of nothing but Discard, would
  // show them an action they do not have.
  if (!canManage) return null;

  return (
    <SaveViewControl
      canSave={!forking}
      saving={saving}
      onSave={(via) => {
        void save(via);
      }}
      onSaveAsNewTab={() => {
        void handleSaveAsNew();
      }}
      onDiscard={discardChanges}
    />
  );
}
